import {ChangeDetectionStrategy, Component, computed, effect, inject, input, model} from "@angular/core"
import {toSignal} from "@angular/core/rxjs-interop"
import {ConfigInfo, InterfaceDescriptor, isConfigInput, VariantInfo} from "@cm/template-nodes"
import {ConfigButtonComponent} from "@common/components/viewers/configurator/config-menu/icons-accordion-menu/config-button/config-button.component"
import {HorizontalScrollArrowsDirective} from "@common/components/viewers/configurator/config-menu/mobile-tabs-menu/horizontal-scroll-arrows.directive"
import {ConfigMenuService} from "@common/components/viewers/configurator/config-menu/services/config-menu.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-mobile-tabs-menu",
    templateUrl: "./mobile-tabs-menu.component.html",
    styleUrls: ["./mobile-tabs-menu.component.scss"],
    imports: [ConfigButtonComponent, HorizontalScrollArrowsDirective],
})
export class MobileTabsMenuComponent {
    readonly $interfaceDescriptors = input.required<InterfaceDescriptor<unknown, object>[]>({alias: "interfaceDescriptors"})
    readonly $tooltipContainer = input.required<HTMLDivElement | undefined>({alias: "tooltipContainer"})
    readonly $useCaptions = input(false, {alias: "useCaptions"})

    private readonly configMenuService = inject(ConfigMenuService)
    readonly $iconSize = toSignal(this.configMenuService.iconSize$)

    readonly $selectedDescriptorIds = model.required<Set<string>>({alias: "selectedDescriptorIds"})

    readonly $configDescriptors = computed(() => {
        return this.$interfaceDescriptors().filter((desc): desc is ConfigInfo => isConfigInput(desc))
    })

    readonly $activeGroup = computed(() => {
        const ids = this.$selectedDescriptorIds()
        const descriptors = this.$configDescriptors()
        const firstSelected = descriptors.find((desc) => ids.has(desc.props.id))
        return firstSelected ?? descriptors.at(0) ?? null
    })

    constructor() {
        effect(() => {
            const descriptors = this.$configDescriptors()
            if (descriptors.length === 0) return
            const current = this.$selectedDescriptorIds()
            const hasConfigSelection = descriptors.some((desc) => current.has(desc.props.id))
            if (!hasConfigSelection) {
                const next = new Set(current)
                next.add(descriptors[0].props.id)
                this.$selectedDescriptorIds.set(next)
            }
        })
    }

    onGroupClicked(id: string): void {
        this.$selectedDescriptorIds.set(new Set([id]))
    }

    onVariantClicked(config: ConfigInfo, variant: VariantInfo): void {
        this.configMenuService.emitConfigSelected({config, variant})
    }
}
